'use client'
import { useMemo, memo } from 'react'
import { useModal, useSession, useTranslator } from '@/hooks'
import { useCart } from '@/context/project/CartContext'
import { usePathname, useRouter } from 'next/navigation'
import styled, { css } from 'styled-components'
import { motion, type Variants } from 'motion/react'
import { currencyFormatter, DY_GUEST_CART_ALLOWED, IS_MMF_STORE, LANG_COUNTRY } from '@/utils'
import { mq, vw, getTransition, hideScrollbar, presets, rgbAlpha } from '@/styles'
import { Button } from '@/components/base/Button'
import { CartSummaryItem } from './CartSummaryItem'
import { MODALS } from '@/context/base/ModalsContext'

const getProducts = (data, push, urlHref) =>
  data.map((item, idx) => (
    <CartSummaryItem key={`cartitem-${idx}`} cartLine={item} id={`cartitem-${idx}`} onClick={() => push(urlHref)} />
  ))

const calculateCartTotal = cartLines => {
  if (!cartLines?.length) return 0
  return cartLines.reduce((total, line) => {
    const price = line.variant?.price?.salePrice || line.variant?.price?.retailPrice || 0
    return total + price * line.quantity
  }, 0)
}

const cartUrl = { cart_url: '/cart', checkout_url: 'https://localhost:3000/checkout' }

export const CartSummaryDropdown = memo(() => {
  const { updatePayload:updateAuthPayload } = useModal(MODALS.AUTH)
  const { t } = useTranslator()
  const { cartLines } = useCart()
	const { user } = useSession()
  const { push } = useRouter()
  const pathname = usePathname()

  const Items = useMemo(() => (cartLines?.length > 0 ? getProducts(cartLines, push, cartUrl.cart_url) : null), [cartLines, push])

  const cartTotal = useMemo(() => calculateCartTotal(cartLines), [cartLines])

	const onClickCta = () => {
		if(DY_GUEST_CART_ALLOWED && !user) {
			updateAuthPayload({ payload: { level: IS_MMF_STORE ? 'login' : 'login_hm' }, open: true })
		} else push('/cart')
	}

  if (!Items) return null

  return (
    <Wrapper variants={listVariants} initial='inactive' animate={'enter'} exit={'exit'}>
      <Dl key='itemsincart' variants={listVariants} initial='inactive' animate={'enter'} exit={'exit'}>
        {Items}
        <Summary key={'summary'} variants={itemVariants}>
          <span>
            {t('project.cart_summary_dropdown.total')} ({cartLines.length})
          </span>
          <span>{currencyFormatter({ price: cartTotal, langCountry: LANG_COUNTRY, round: false, trailingZeros: true })}</span>
          {!pathname.includes('cart') && (
            <Button onClick={onClickCta} outline={true} bgColor='--color-text' color='--color-text'>
              {t('project.cart_summary_dropdown.show_cart')}
            </Button>
          )}
        </Summary>
      </Dl>
    </Wrapper>
  )
})

export const Wrapper = styled(motion.div)`
  font-family: var(--font-graphik);
  position: relative;
  z-index: 9999;

  &:before {
    border-bottom: ${vw(10, 'mobile')} solid var(--color-light-grey);
    border-left: ${vw(9, 'mobile')} solid transparent;
    border-right: ${vw(9, 'mobile')} solid transparent;
    content: '';
    height: 0;
    position: absolute;
    right: ${vw(20, 'mobile')};
    top: -${vw(8, 'mobile')};
    width: 0;

    ${mq.greaterThan('nexus7')} {
      border-bottom: ${vw(10, 'tablet')} solid var(--color-light-grey);
      border-left: ${vw(9, 'tablet')} solid transparent;
      border-right: ${vw(9, 'tablet')} solid transparent;
      top: -${vw(8, 'tablet')};
      right: ${vw(20, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      border-bottom: ${vw(10, 'desktop')} solid var(--color-light-grey);
      border-left: ${vw(9, 'desktop')} solid transparent;
      border-right: ${vw(9, 'desktop')} solid transparent;
      top: -${vw(8, 'desktop')};
      right: ${vw(20, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      border-bottom: 10px solid var(--color-light-grey);
      border-left: 9px solid transparent;
      border-right: 9px solid transparent;
      top: -8px;
      right: 20px;
    }
  }
`

export const Dl = styled(motion.dl)`
  border-radius: 20px;
  box-shadow: 0 10px 20px 0 ${rgbAlpha('var(--color-black)', 0.2)};
  display: flex;
  flex-wrap: wrap;
  height: auto;
  justify-content: flex-start;
  width: ${vw(308, 'mobile')};
  position: relative;
  overflow: scroll;
  background-color: var(--color-light-grey);

  ${hideScrollbar()}

  ${mq.greaterThan('nexus7')} {
    width: ${vw(308, 'tablet')};
  }

  ${mq.greaterThan('tablet')} {
    display: flex;
    max-height: ${vw(370, 'desktop')};
    width: ${vw(308, 'desktop')};
  }

  ${mq.greaterThan('desktop')} {
    max-height: 370px;
    width: 308px;
  }
`

export const Summary = styled(motion.dt)<{ $type?: 'new' | string }>`
  ${presets.font20}
  align-items: center;
  background-color: var(--color-light-grey);
  border-top: 1px solid ${rgbAlpha('--color-text', 0.6)};
  bottom: 0;
  color: var(--color-text);
  display: flex;
  display: grid;
  font-weight: bold;
  grid-column-gap: ${vw(10, 'mobile')};
  grid-row-gap: ${vw(15, 'mobile')};
  grid-template-columns: 1fr 1fr;
  padding: ${vw(18, 'mobile')} ${vw(18, 'mobile')} ${vw(22, 'mobile')};
  position: sticky;
  width: 100%;

  ${mq.greaterThan('nexus7')} {
    padding: ${vw(18, 'tablet')} ${vw(18, 'tablet')} ${vw(22, 'tablet')};
    grid-column-gap: ${vw(10, 'tablet')};
    grid-row-gap: ${vw(15, 'tablet')};
  }

  ${mq.greaterThan('tablet')} {
    padding: ${vw(18, 'desktop')} ${vw(18, 'desktop')} ${vw(22, 'desktop')};
    grid-column-gap: ${vw(10, 'desktop')};
    grid-row-gap: ${vw(15, 'desktop')};
  }

  ${mq.greaterThan('desktop')} {
    padding: 18px 18px 22px;
    grid-column-gap: 10px;
    grid-row-gap: 15px;
  }

  ${({ $type }) =>
    $type === 'new' &&
    css`
      border-bottom-left-radius: 20px;
      border-bottom-right-radius: 20px;
    `};

  > span {
    ${presets.font18_1}
    font-weight: 700;
    color: var(--color-text);
  }
  > span:first-child {
    color: var(--color-text--darker);
    font-weight: 600;
  }
  > span:nth-child(2) {
    justify-self: end;
    color: var(--color-text);
  }

  > button {
    font-weight: 700;
    height: auto;
    margin-top: ${vw(10, 'mobile')};
    white-space: nowrap;
    width: fit-content;
    padding: ${vw(10, 'mobile')} ${vw(30, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      margin-top: ${vw(10, 'tablet')};
      padding: ${vw(10, 'tablet')} ${vw(30, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      margin-top: ${vw(10, 'desktop')};
      padding: ${vw(12, 'desktop')} ${vw(35, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      margin-top: 10px;
      padding: 12px 35px;
    }

    span {
      ${presets.font13}
    }
  }
`

export const listVariants: Variants = {
  inactive: { opacity: 0, ...getTransition(0.5) },
  enter: { opacity: 1, transition: { ...getTransition(0.5), staggerChildren: 0.01 } },
  exit: { opacity: 0, ...getTransition(0.5) },
}

export const itemVariants: Variants = {
  inactive: { y: -10, opacity: 0, ...getTransition(0.5) },
  enter: { y: 0, opacity: 1, ...getTransition(0.5) },
  exit: { y: -10, opacity: 0, ...getTransition(0.5) },
}
